import { useCallback } from 'react';
import { PhoneCall, PackageCustomer, CustomerAddress } from '../types';
import { ShippingAddressDto } from '../../../../services/phoneCallService';

export interface UsePhoneHandlersDeps {
  manualPhoneNumber: string;
  incomingCall: PhoneCall | null;
  currentCustomer: PackageCustomer | null;
  setSnackbar: (data: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
  setIsSearchingPhone: (v: boolean) => void;
  setIsManualPhoneModalOpen: (v: boolean) => void;
  setCustomerPhoneFilter: (v: string | null) => void;
  setCustomerNameFilter: (v: string | null) => void;
  setCurrentCustomer: React.Dispatch<React.SetStateAction<PackageCustomer | null>>;
  setSelectedAddressId: (v: number | null) => void;
  setIsCallAnswered: (v: boolean) => void;
  setNewCustomerPhone: (v: string) => void;
  setAddCustomerModalOpen: (v: boolean) => void;
  searchCustomerByPhone: (phone: string) => Promise<any>;
}

export const usePhoneHandlers = (deps: UsePhoneHandlersDeps) => {
  const {
    manualPhoneNumber,
    incomingCall,
    currentCustomer,
    setSnackbar,
    setIsSearchingPhone,
    setIsManualPhoneModalOpen,
    setCustomerPhoneFilter,
    setCustomerNameFilter,
    setCurrentCustomer,
    setSelectedAddressId,
    setIsCallAnswered,
    setNewCustomerPhone,
    setAddCustomerModalOpen,
    searchCustomerByPhone,
  } = deps;

  const handleManualPhoneSearch = useCallback(async () => {
    // ✅ Telefon kuralı esnetildi: 4-15 hane arası serbest (uluslararası numara
    // ve standart dışı formatlara izin verecek şekilde). Modal tarafında da
    // aynı limit kontrol ediliyor; burada savunma katmanı olarak kalıyor.
    if (!manualPhoneNumber || manualPhoneNumber.length < 4 || manualPhoneNumber.length > 15) {
      setSnackbar({
        open: true,
        message: 'Lütfen geçerli bir telefon numarası girin (4-15 hane)',
        severity: 'warning',
      });
      return;
    }

    setIsSearchingPhone(true);

    try {
      console.log('📱 [ManualPhone] Telefon numarası aranıyor:', manualPhoneNumber);
      const customerResult = await searchCustomerByPhone(manualPhoneNumber);

      const found = customerResult?.found || customerResult?.Found;

      if (found) {
        const addresses = customerResult?.addresses || customerResult?.Addresses || [];
        const customerName = customerResult?.customerName || customerResult?.CustomerName || manualPhoneNumber;
        const customerId = customerResult?.accountId || customerResult?.AccountId;

        console.log('✅ [ManualPhone] Müşteri bulundu:', { customerName, customerId, addressCount: addresses.length });

        setCustomerPhoneFilter(manualPhoneNumber);
        setCustomerNameFilter(customerName);

        const mappedAddresses = addresses.map((a: any) => {
          const addrId = a.id || a.Id;
          const addrLabel = a.addressLabel || a.AddressLabel || 'Adres';
          const district = a.district || a.District || '';
          const city = a.city || a.City || '';
          const address = a.address || a.Address || '';
          const isDefault = a.isDefault ?? a.IsDefault ?? false;
          return {
            id: addrId,
            label: addrLabel,
            address: address,
            fullAddress: `${address}${district ? ', ' + district : ''}${city ? ', ' + city : ''}`.trim(),
            isDefault: isDefault,
            city,
            district,
            mahalle: a.mahalle || a.Mahalle || '',
            cadde: a.cadde || a.Cadde || '',
            sokak: a.sokak || a.Sokak || '',
            apartman: a.apartman || a.Apartman || '',
            siteAdi: a.siteAdi || a.SiteAdi || '',
            blok: a.blok || a.Blok || '',
            binaNo: a.binaNo || a.BinaNo || '',
            kat: a.kat || a.Kat || '',
            daireNo: a.daireNo || a.DaireNo || '',
            deliveryNotes: a.deliveryNotes || a.DeliveryNotes || '',
            authorizedPerson: a.authorizedPerson || a.AuthorizedPerson || '',
            phone: a.phone || a.Phone || a.mobilePhone || a.MobilePhone || '',
          };
        });

        setCurrentCustomer({
          id: customerId,
          name: customerName,
          phone: manualPhoneNumber,
          addresses: mappedAddresses,
          totalOrders: 0,
          lastOrderDate: '',
        });

        const defaultAddr = mappedAddresses.find((a: any) => a.isDefault) || mappedAddresses[0];
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
        }

        setIsCallAnswered(true);

        setIsManualPhoneModalOpen(false);
        setSnackbar({ open: true, message: `✅ Müşteri bulundu: ${customerName}`, severity: 'success' });
      } else {
        console.log('❌ [ManualPhone] Müşteri bulunamadı:', manualPhoneNumber);
        setSnackbar({
          open: true,
          message: `Bu telefon numarası kayıtlı değil. Yeni müşteri olarak kayıt edebilirsiniz.`,
          severity: 'warning',
        });

        setIsManualPhoneModalOpen(false);
        setNewCustomerPhone(manualPhoneNumber);
        setAddCustomerModalOpen(true);
      }
    } catch (error) {
      console.error('❌ [ManualPhone] Arama hatası:', error);
      setSnackbar({ open: true, message: 'Telefon numarası aranamadı', severity: 'error' });
    } finally {
      setIsSearchingPhone(false);
    }
  }, [
    manualPhoneNumber,
    setSnackbar,
    setIsSearchingPhone,
    setIsManualPhoneModalOpen,
    setCustomerPhoneFilter,
    setCustomerNameFilter,
    setCurrentCustomer,
    setSelectedAddressId,
    setIsCallAnswered,
    setNewCustomerPhone,
    setAddCustomerModalOpen,
    searchCustomerByPhone,
  ]);

  const handleSelectRecentCall = useCallback(async (call: PhoneCall) => {
    if (incomingCall) {
      console.log('📞 [RecentCalls] Aktif arama var, son aramalardan seçim engellendi');
      return;
    }

    console.log('📞 [RecentCalls] Son aramadan müşteri seçildi:', call.phone);

    setCustomerPhoneFilter(call.phone);
    setCustomerNameFilter(call.customerName || call.phone);

    const customerResult = await searchCustomerByPhone(call.phone);

    if (customerResult?.found || customerResult?.Found) {
      const addresses = customerResult.addresses || customerResult.Addresses || [];
      const mappedAddresses = addresses.map((a: any) => {
        const addrId = a.id || a.Id;
        const addrLabel = a.addressLabel || a.AddressLabel || 'Adres';
        const district = a.district || a.District || '';
        const city = a.city || a.City || '';
        const address = a.address || a.Address || '';
        const isDefault = a.isDefault ?? a.IsDefault ?? false;

        return {
          id: addrId,
          label: addrLabel,
          address: `${district}, ${city} - ${address}`,
          isDefault,
          city,
          district,
          mahalle: a.mahalle || a.Mahalle || '',
          cadde: a.cadde || a.Cadde || '',
          sokak: a.sokak || a.Sokak || '',
          apartman: a.apartman || a.Apartman || '',
          siteAdi: a.siteAdi || a.SiteAdi || '',
          blok: a.blok || a.Blok || '',
          binaNo: a.binaNo || a.BinaNo || '',
          kat: a.kat || a.Kat || '',
          daireNo: a.daireNo || a.DaireNo || '',
          deliveryNotes: a.deliveryNotes || a.DeliveryNotes || '',
          authorizedPerson: a.authorizedPerson || a.AuthorizedPerson || '',
          phone: a.phone || a.Phone || a.mobilePhone || a.MobilePhone || '',
        };
      });

      const customerId = customerResult.accountId || customerResult.AccountId;
      const customerName = customerResult.customerName || customerResult.CustomerName;

      setCurrentCustomer({
        id: customerId,
        name: customerName,
        phone: customerResult.phone || customerResult.Phone || customerResult.mobilePhone || customerResult.MobilePhone,
        addresses: mappedAddresses,
        totalOrders: customerResult.totalOrders || customerResult.TotalOrders || 0,
        lastOrderDate: customerResult.lastOrderDate || customerResult.LastOrderDate || '',
      });

      const defaultAddress = mappedAddresses.find((a: any) => a.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else if (mappedAddresses.length > 0) {
        setSelectedAddressId(mappedAddresses[0].id);
      }

      console.log('📞 [RecentCalls] Müşteri yüklendi:', customerName, 'Adres sayısı:', mappedAddresses.length);
    } else {
      setCurrentCustomer({
        id: 0,
        name: call.customerName || call.phone,
        phone: call.phone,
        addresses: [],
        totalOrders: 0,
        lastOrderDate: '',
      });
      setSelectedAddressId(null);
      console.log('📞 [RecentCalls] Müşteri bulunamadı, yeni müşteri:', call.phone);
    }
  }, [
    incomingCall,
    setCustomerPhoneFilter,
    setCustomerNameFilter,
    searchCustomerByPhone,
    setCurrentCustomer,
    setSelectedAddressId,
  ]);

  const handleAddressAdded = useCallback((newAddress: ShippingAddressDto) => {
    console.log('📍 [Address] Yeni adres eklendi, müşteri güncelleniyor:', newAddress);

    if (currentCustomer) {
      const newCustomerAddress: CustomerAddress = {
        id: newAddress.Id,
        label: newAddress.AddressLabel || 'Adres',
        address: `${newAddress.District}, ${newAddress.City} - ${newAddress.Address}`,
        isDefault: newAddress.IsDefault || false,
        city: newAddress.City || '',
        district: newAddress.District || '',
        mahalle: newAddress.Mahalle || '',
        cadde: newAddress.Cadde || '',
        sokak: newAddress.Sokak || '',
        apartman: newAddress.Apartman || '',
        siteAdi: newAddress.SiteAdi || '',
        blok: newAddress.Blok || '',
        binaNo: newAddress.BinaNo || '',
        kat: newAddress.Kat || '',
        daireNo: newAddress.DaireNo || '',
        deliveryNotes: newAddress.DeliveryNotes || '',
        authorizedPerson: newAddress.AuthorizedPerson || '',
        phone: newAddress.Phone || newAddress.MobilePhone || '',
      };

      setCurrentCustomer(prev => {
        if (!prev) return prev;

        let updatedAddresses = prev.addresses;
        if (newCustomerAddress.isDefault) {
          updatedAddresses = prev.addresses.map(addr => ({ ...addr, isDefault: false }));
        }

        return {
          ...prev,
          addresses: [...updatedAddresses, newCustomerAddress],
        };
      });

      setSelectedAddressId(newCustomerAddress.id);

      console.log('✅ [Address] Müşteri adresleri güncellendi');
    }
  }, [currentCustomer, setCurrentCustomer, setSelectedAddressId]);

  return {
    handleManualPhoneSearch,
    handleSelectRecentCall,
    handleAddressAdded,
  };
};
